/**
 * RequestInfoForm — public solicitation form for demos, grants, pricing.
 *
 * UNAUTHENTICATED — submits to submitContactRequest callable.
 * Includes honeypot field (display:none) for spam filtering.
 * Reusable: mounted on LandingPage and ContactPage.
 */

import { useState } from 'react';
import { submitContactRequest } from '../../services/contact';

const INTEREST_OPTIONS = [
  { value: '', label: 'What are you interested in?' },
  { value: 'demo', label: 'Department demo' },
  { value: 'grant', label: 'Grant support' },
  { value: 'insurance', label: 'Insurance letter' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'other', label: 'Other' },
] as const;

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

interface Props {
  /** Variant styles for embedding context. Defaults to landing (dark). */
  variant?: 'landing' | 'page';
}

export function RequestInfoForm({ variant = 'landing' }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [organization, setOrganization] = useState('');
  const [usState, setUsState] = useState('');
  const [interest, setInterest] = useState('');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const canSubmit =
    status !== 'submitting' &&
    name.trim() !== '' &&
    email.trim() !== '' &&
    message.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('submitting');
    setErrorMsg('');
    try {
      await submitContactRequest({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        organization: organization.trim(),
        state: usState.trim(),
        interest,
        message: message.trim(),
        website: honeypot,
      });
      setStatus('success');
    } catch (err: unknown) {
      const code =
        err && typeof err === 'object' && 'code' in err
          ? (err as { code: string }).code
          : '';
      if (code === 'functions/resource-exhausted') {
        setErrorMsg(
          'Too many requests — email alexi@copapp.ai instead.',
        );
      } else {
        setErrorMsg('Something went wrong. Please try again.');
      }
      setStatus('error');
    }
  };

  // Landing variant uses white-on-dark; page variant uses the gray-950 card.
  const isLanding = variant === 'landing';
  const inputCls = isLanding
    ? 'w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:border-blue-400'
    : 'w-full bg-gray-900/50 border border-gray-800 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-copapp-400';

  if (status === 'success') {
    return (
      <section id="request-info" className={isLanding ? 'py-16 border-t border-white/10' : 'mt-10'}>
        <div className={isLanding ? 'max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center' : 'text-center'}>
          <div className={`rounded-xl p-8 ${isLanding ? 'bg-white/5 border border-white/10' : 'bg-gray-900/50 border border-gray-800'}`}>
            <p className="text-lg font-semibold text-white mb-2">Received.</p>
            <p className={`text-sm ${isLanding ? 'text-white/70' : 'text-gray-300'}`}>
              You&apos;ll hear from Alexi directly &mdash; usually same day.
            </p>
            <p className={`text-xs mt-3 ${isLanding ? 'text-white/40' : 'text-gray-500'}`}>
              Responses come from alexi@copapp.ai
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="request-info" className={isLanding ? 'py-16 border-t border-white/10' : 'mt-10'}>
      <div className={isLanding ? 'max-w-2xl mx-auto px-4 sm:px-6 lg:px-8' : ''}>
        <div className="text-center mb-8">
          <h2 className={`text-2xl font-bold ${isLanding ? 'text-white' : 'text-white'} mb-2`}>
            Get in touch
          </h2>
          <p className={`text-sm ${isLanding ? 'text-white/60' : 'text-gray-400'}`}>
            Demo, pricing, grant help, or just a question &mdash; we respond fast.
          </p>
        </div>

        <form
          onSubmit={(e) => void handleSubmit(e)}
          className={`rounded-xl p-6 sm:p-8 space-y-4 ${isLanding ? 'bg-white/5 border border-white/10' : 'bg-gray-900/50 border border-gray-800'}`}
        >
          {/* Honeypot — hidden from humans */}
          <div style={{ display: 'none' }} aria-hidden="true">
            <label htmlFor="company_website">Company Website</label>
            <input
              id="company_website"
              name="company_website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Name *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={inputCls}
            />
            <input
              type="email"
              placeholder="Email *"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={inputCls}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <input
              type="tel"
              placeholder="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={inputCls}
            />
            <input
              type="text"
              placeholder="Department / Organization"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              className={inputCls}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="State (e.g. AL)"
              value={usState}
              onChange={(e) => setUsState(e.target.value)}
              maxLength={2}
              className={inputCls}
            />
            <select
              value={interest}
              onChange={(e) => setInterest(e.target.value)}
              className={inputCls}
            >
              {INTEREST_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <textarea
            placeholder="Message *"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={4}
            className={`${inputCls} resize-none`}
          />

          {status === 'error' && errorMsg && (
            <p className="text-xs text-red-400">{errorMsg}</p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:hover:bg-blue-600 rounded-lg text-sm font-semibold text-white transition-colors"
          >
            {status === 'submitting' ? 'Sending...' : 'Send request'}
          </button>
        </form>
      </div>
    </section>
  );
}
